/**
 * Hero media band - Figma `6377:87431`, `6377:88580` (and `6377:87460`, `6377:87488`).
 *
 * A full-width photograph across the content grid, `Radius/l`, with the aspect ratio
 * the editor picked on the media component.
 *
 * Two things the spec got wrong, found by opening the design:
 *
 * 1. **There is no overlaid title.** The spec calls this a "full-width photo band with
 *    an overlaid page title" and marks `title` required, but neither inspected instance
 *    shows a title anywhere on the band. `title` is now optional and is used as the
 *    image's accessible name when the media component has no `alt` of its own - so it
 *    still earns its place, without inventing a caption the design does not have.
 * 2. **One instance has a play button** (`6377:87431`), which implies video. The media
 *    field is `allowedTypes: ['images']`, so there is nothing to play. A play button
 *    that does nothing is worse than none, so it is not drawn. If the client wants
 *    video here the content model needs a video field - see docs/section-inventory.md.
 *
 * The `icon` field is therefore not rendered. It is left in the model rather than
 * removed, because dropping a field is the one change that loses an editor's work.
 */
import { SectionShell } from '../primitives';
import type { HeroMediaSection } from '../../types/sections';
import { SIZES } from '../../components/ui/Image';
import { MediaFrame, aspectClass, hasMedia } from '../../components/ui/MediaFrame';

export default function HeroMedia({ data }: { data: HeroMediaSection }) {
  if (!hasMedia(data.media)) return null;

  // 1360x760 is what the band measures in the file - 6377:88580, and again at
  // 3883:71072 - so 16:9 is the slot's own shape rather than a guess. This used to map
  // `wide` to 21:9, which nothing in the file is.
  const aspect = aspectClass(data.media) ?? 'aspect-[16/9]';

  return (
    <SectionShell settings={data.settings} name="hero-media">
      <figure className={`relative m-0 overflow-hidden rounded-l bg-surface-sunken ${aspect}`}>
        <MediaFrame media={data.media} sizes={SIZES.full} title={data.title ?? undefined} />
        {/* The chip the design floats over the band (3787:60188), naming what the film
            is. `pointer-events-none` so it never sits between a visitor and the play
            button underneath it. */}
        {data.icon ? (
          <figcaption className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center">
            <span className="rounded-full bg-black/50 px-6 py-2 text-caption text-white backdrop-blur-[7px]">{data.icon}</span>
          </figcaption>
        ) : null}
      </figure>
    </SectionShell>
  );
}
